import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatErr } from "../lib/api";
import {
  createProduct,
  removeProduct,
  subscribeProducts,
  updateProduct,
  addImageToGallery,
  removeImageFromGallery,
  reorderGallery,
} from "../lib/firestoreProducts";
import { uploadProductImage } from "../lib/firestoreStorage";
import { subscribeInquiries, updateInquiryStatus } from "../lib/firestoreInquiries";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X } from "lucide-react";

const EMPTY = {
  name: "",
  description: "",
  price: 50,
  image_url: "",
  category: "adult",
  embroidery_detail: "",
  stock: 100,
  featured: false,
  gallery: [],
};

export default function AdminPage() {
  const { loading, isAdminAuthenticated, adminEmail, logoutAdmin } = useAuth();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [inquiryStatusFilter, setInquiryStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [galleryImageUrl, setGalleryImageUrl] = useState("");

  useEffect(() => {
    if (!isAdminAuthenticated) return;
    const unsub = subscribeProducts(
      (items) => setProducts(items),
      (error) => toast.error(formatErr(error))
    );
    return () => unsub();
  }, [isAdminAuthenticated]);

  useEffect(() => {
    if (!isAdminAuthenticated) return;
    const unsubscribe = subscribeInquiries(
      (items) => setInquiries(items),
      (error) => toast.error(formatErr(error))
    );

    return () => unsubscribe();
  }, [isAdminAuthenticated]);

  if (loading) return <div className="pt-32 px-6">Loading…</div>;
  if (!isAdminAuthenticated) return <Navigate to="/admin/login" replace />;

  const save = async () => {
    try {
      const payload = { ...editing, price: Number(editing.price), stock: Number(editing.stock) };
      if (!payload.image_url) {
        toast.error("Please upload a product image");
        return;
      }
      if (editing.id) {
        await updateProduct(editing.id, payload);
        toast.success("Updated");
      } else {
        await createProduct(payload);
        toast.success("Created");
      }
      setEditing(null);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const onImageSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const uploadedUrl = await uploadProductImage(file);
      setEditing((prev) => ({ ...prev, image_url: uploadedUrl }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setUploadingImage(false);
      event.target.value = "";
    }
  };

  const addGalleryImageUrl = () => {
    if (!galleryImageUrl.trim()) {
      toast.error("Please enter an image URL");
      return;
    }
    if (!editing) return;
    const currentGallery = Array.isArray(editing.gallery) ? editing.gallery : [];
    setEditing((prev) => ({ ...prev, gallery: [...currentGallery, galleryImageUrl.trim()] }));
    setGalleryImageUrl("");
    toast.success("Gallery image added");
  };

  const removeGalleryImage = (imageUrl) => {
    if (!editing) return;
    const updated = (editing.gallery || []).filter((url) => url !== imageUrl);
    setEditing((prev) => ({ ...prev, gallery: updated }));
  };

  const moveGalleryImage = (fromIndex, toIndex) => {
    if (!editing || !Array.isArray(editing.gallery)) return;
    const gallery = [...editing.gallery];
    const [moved] = gallery.splice(fromIndex, 1);
    gallery.splice(toIndex, 0, moved);
    setEditing((prev) => ({ ...prev, gallery }));
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await removeProduct(id);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const markInquiry = async (id, status) => {
    try {
      await updateInquiryStatus(id, status);
      toast.success(`Marked as ${status}`);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const filteredInquiries = inquiries.filter((q) => {
    if (inquiryStatusFilter === "all") return true;
    return (q.status || "pending") === inquiryStatusFilter;
  });

  return (
    <div className="pt-24 px-6 md:px-12 pb-20 min-h-screen" data-testid="admin-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="label-eyebrow mb-3">Atelier control</div>
          <h1 className="font-display text-4xl md:text-6xl tracking-tighter">Admin.</h1>
          {adminEmail && <p className="mt-2 text-sm text-black/50">Signed in as {adminEmail}</p>}
          <p className="mt-2 text-sm text-black/50">Product and inventory data is now powered by Firestore.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={logoutAdmin}
            className="px-4 py-2 text-sm border border-black/30 hover:bg-black hover:text-white transition-colors"
          >
            Sign out
          </button>
          <button
            onClick={() => setTab("products")}
            data-testid="tab-products"
            className={`px-4 py-2 text-sm ${tab === "products" ? "bg-black text-white" : "border border-black/30"}`}
          >
            Products ({products.length})
          </button>
          <button
            onClick={() => setTab("inquiries")}
            data-testid="tab-inquiries"
            className={`px-4 py-2 text-sm ${tab === "inquiries" ? "bg-black text-white" : "border border-black/30"}`}
          >
            Inquiries ({inquiries.length})
          </button>
        </div>
      </div>

      {tab === "products" && (
      <div>
        <button
          onClick={() => setEditing({ ...EMPTY })}
          data-testid="new-product-btn"
          className="mb-6 inline-flex items-center gap-2 bg-black text-white px-5 py-3 text-sm hover:bg-[#C9A961] transition-colors"
        >
          <Plus size={14} /> New product
        </button>

        <div className="overflow-x-auto border border-black/10">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F5F0]">
              <tr>
                <th className="text-left p-3 label-eyebrow">Image</th>
                <th className="text-left p-3 label-eyebrow">Name</th>
                <th className="text-left p-3 label-eyebrow">Category</th>
                <th className="text-left p-3 label-eyebrow">Price</th>
                <th className="text-left p-3 label-eyebrow">Stock</th>
                <th className="text-left p-3 label-eyebrow">Featured</th>
                <th className="text-right p-3 label-eyebrow">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t border-black/10" data-testid={`admin-product-${p.id}`}>
                  <td className="p-3"><img src={p.image_url} alt={p.name} className="w-12 h-12 object-cover" /></td>
                  <td className="p-3 font-display">{p.name}</td>
                  <td className="p-3">{p.category}</td>
                  <td className="p-3">{`₹${p.price.toFixed(2)}`}</td>
                  <td className="p-3">{p.stock}</td>
                  <td className="p-3">{p.featured ? "Yes" : "—"}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => setEditing(p)} className="inline-flex items-center gap-1 hover:text-[#C9A961] mr-3"><Pencil size={14} /> Edit</button>
                    <button onClick={() => del(p.id)} className="inline-flex items-center gap-1 hover:text-red-600"><Trash2 size={14} /> Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {tab === "inquiries" && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setInquiryStatusFilter("all")}
              className={`px-3 py-1 text-sm border ${inquiryStatusFilter === "all" ? "bg-black text-white border-black" : "border-black/30"}`}
              data-testid="inq-filter-all"
            >
              All ({inquiries.length})
            </button>
            <button
              onClick={() => setInquiryStatusFilter("pending")}
              className={`px-3 py-1 text-sm border ${inquiryStatusFilter === "pending" ? "bg-black text-white border-black" : "border-black/30"}`}
              data-testid="inq-filter-pending"
            >
              Pending ({inquiries.filter((q) => (q.status || "pending") === "pending").length})
            </button>
            <button
              onClick={() => setInquiryStatusFilter("resolved")}
              className={`px-3 py-1 text-sm border ${inquiryStatusFilter === "resolved" ? "bg-black text-white border-black" : "border-black/30"}`}
              data-testid="inq-filter-resolved"
            >
              Resolved ({inquiries.filter((q) => (q.status || "pending") === "resolved").length})
            </button>
          </div>

          {filteredInquiries.length === 0 && <div className="text-black/50">No inquiries found for this filter.</div>}
          {filteredInquiries.map((q) => (
            <div key={q.id} data-testid={`inquiry-${q.id}`} className="border border-black/10 p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="label-eyebrow text-black/50">
                    {q.created_at ? new Date(q.created_at).toLocaleString() : "Just now"}
                  </div>
                  <h3 className="font-display text-2xl mt-1">
                    {q.source === "contact" ? "Contact Form" : (q.company || "Custom & Bulk")}
                  </h3>
                  <div className="text-sm text-black/60">
                    {q.contact_name && <span>{q.contact_name} · </span>}
                    {q.email || "No email"}
                    {q.phone && <span> · {q.phone}</span>}
                    {q.whatsapp && <span> · WhatsApp {q.whatsapp}</span>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 text-sm items-center">
                  <span className="border border-black px-3 py-1 capitalize">{q.source === "contact" ? "Contact" : "Custom & Bulk"}</span>
                  {q.use_case && <span className="border border-black px-3 py-1">{q.use_case}</span>}
                  {q.quantity > 0 && <span className="border border-black px-3 py-1">{q.quantity} units</span>}
                  {q.budget && <span className="border border-black px-3 py-1">{q.budget}</span>}
                  <span className={`border px-3 py-1 capitalize ${q.status === "resolved" ? "border-green-700 text-green-700" : "border-amber-700 text-amber-700"}`}>
                    {q.status || "pending"}
                  </span>
                </div>
              </div>

              {(q.instagram || q.facebook) && (
                <div className="mt-4 text-sm text-black/70 flex flex-wrap gap-4">
                  {q.instagram && <span>Instagram: {q.instagram}</span>}
                  {q.facebook && <span>Facebook: {q.facebook}</span>}
                </div>
              )}

              <p className="mt-4 text-sm leading-relaxed border-t border-black/10 pt-4 whitespace-pre-wrap">{q.message || "No message."}</p>

              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => markInquiry(q.id, "pending")}
                  className={`px-3 py-1 text-sm border ${q.status === "pending" ? "bg-black text-white border-black" : "border-black/30"}`}
                >
                  Mark Pending
                </button>
                <button
                  onClick={() => markInquiry(q.id, "resolved")}
                  className={`px-3 py-1 text-sm border ${q.status === "resolved" ? "bg-black text-white border-black" : "border-black/30"}`}
                >
                  Mark Resolved
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="product-modal">
            <div className="p-6 md:p-8 flex items-center justify-between border-b border-black/10">
              <h2 className="font-display text-2xl">{editing.id ? "Edit product" : "New product"}</h2>
              <button onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            <div className="p-6 md:p-8 space-y-4">
              <Field label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testId="form-name" />
              <Field label="Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} textarea testId="form-desc" />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Price" type="number" value={editing.price} onChange={(v) => setEditing({ ...editing, price: v })} testId="form-price" />
                <Field label="Stock" type="number" value={editing.stock} onChange={(v) => setEditing({ ...editing, stock: v })} testId="form-stock" />
              </div>
              <label className="block">
                <span className="label-eyebrow block mb-2">Product image</span>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="inline-flex items-center justify-center border border-black/30 px-4 py-3 text-sm cursor-pointer hover:bg-black hover:text-white transition-colors">
                    {uploadingImage ? "Uploading..." : "Upload from device"}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={onImageSelected}
                      className="hidden"
                      data-testid="form-image-upload"
                      disabled={uploadingImage}
                    />
                  </label>
                  {editing.image_url && (
                    <a href={editing.image_url} target="_blank" rel="noreferrer" className="text-sm underline">
                      Preview image
                    </a>
                  )}
                </div>
                <input
                  type="text"
                  value={editing.image_url}
                  onChange={(e) => setEditing({ ...editing, image_url: e.target.value })}
                  className="w-full border border-black/30 px-4 py-3 mt-3 focus:border-black focus:outline-none"
                  data-testid="form-image"
                  placeholder="Uploaded image URL will appear here"
                />
              </label>
              <Field label="Embroidery detail" value={editing.embroidery_detail} onChange={(v) => setEditing({ ...editing, embroidery_detail: v })} testId="form-embroidery" />
              <label className="block">
                <span className="label-eyebrow block mb-2">Gallery images</span>
                <div className="mb-4 flex gap-2">
                  <input
                    type="text"
                    value={galleryImageUrl}
                    onChange={(e) => setGalleryImageUrl(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addGalleryImageUrl()}
                    placeholder="Paste image URL and press Enter or click Add"
                    className="flex-1 border border-black/30 px-4 py-3 text-sm focus:border-black focus:outline-none"
                    data-testid="form-gallery-url"
                  />
                  <button
                    onClick={addGalleryImageUrl}
                    className="px-4 py-3 bg-black text-white text-sm hover:bg-[#C9A961] transition-colors whitespace-nowrap"
                    type="button"
                  >
                    Add
                  </button>
                </div>
                {Array.isArray(editing.gallery) && editing.gallery.length > 0 ? (
                  <div className="space-y-3">
                    <p className="text-xs text-black/60 mb-3">Drag to reorder, or use buttons below</p>
                    <div className="grid grid-cols-3 gap-3">
                      {editing.gallery.map((imageUrl, idx) => (
                        <div key={idx} className="relative group">
                          <img src={imageUrl} alt={`Gallery ${idx}`} className="w-full aspect-square object-cover border border-black/30" />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <button
                              onClick={() => moveGalleryImage(idx, Math.max(0, idx - 1))}
                              className="bg-white text-black px-2 py-1 text-xs disabled:opacity-50"
                              disabled={idx === 0}
                              title="Move up"
                              type="button"
                            >
                              ↑
                            </button>
                            <button
                              onClick={() => moveGalleryImage(idx, Math.min(editing.gallery.length - 1, idx + 1))}
                              className="bg-white text-black px-2 py-1 text-xs disabled:opacity-50"
                              disabled={idx === editing.gallery.length - 1}
                              title="Move down"
                              type="button"
                            >
                              ↓
                            </button>
                            <button
                              onClick={() => removeGalleryImage(imageUrl)}
                              className="bg-red-600 text-white px-2 py-1 text-xs"
                              title="Remove"
                              type="button"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-black/50">No gallery images added yet</p>
                )}
              </label>
              <label className="block">
                <span className="label-eyebrow block mb-2">Category</span>
                <select
                  value={editing.category}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  data-testid="form-category"
                  className="w-full border border-black/30 px-4 py-3 bg-white"
                >
                  <option value="adult">Adult</option>
                  <option value="kids">Kids</option>
                  <option value="limited">Limited</option>
                  <option value="apparel">Apparel</option>
                </select>
              </label>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={editing.featured}
                  onChange={(e) => setEditing({ ...editing, featured: e.target.checked })}
                  data-testid="form-featured"
                />
                <span className="text-sm">Featured on home page</span>
              </label>
            </div>
            <div className="p-6 md:p-8 border-t border-black/10 flex gap-3 justify-end">
              <button onClick={() => setEditing(null)} className="px-5 py-2 border border-black/30 text-sm">Cancel</button>
              <button onClick={save} data-testid="form-save" className="px-5 py-2 bg-black text-white text-sm hover:bg-[#C9A961] transition-colors">
                {editing.id ? "Save changes" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", textarea, testId }) {
  const Cls = "w-full border border-black/30 px-4 py-3 focus:border-black focus:outline-none";
  return (
    <label className="block">
      <span className="label-eyebrow block mb-2">{label}</span>
      {textarea ? (
        <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} className={Cls} data-testid={testId} />
      ) : (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className={Cls} data-testid={testId} />
      )}
    </label>
  );
}
